import { Card } from "@/components/ui/card";
import { money } from "@/lib/format";
import { CheckCircle2, AlertTriangle } from "lucide-react";

export default function TotalsPanel({ totals = {} }) {
    const Row = ({ label, value, bold, accent }) => (
        <div className={`flex items-center justify-between text-sm ${bold ? "font-semibold" : ""} ${accent ? "text-[#0F2A47]" : "text-slate-700"}`}>
            <span>{label}</span>
            <span className="tabular">{value}</span>
        </div>
    );

    return (
        <Card className="rounded-xl border-slate-200 p-5 lg:sticky lg:top-6 space-y-4" data-testid="totals-panel">
            <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Customer-facing</div>
                <div className="mt-2 space-y-2">
                    <Row label="Home" value={money(totals.home_total ?? totals.home_subtotal ?? 0)} />
                    <Row label="Delivery" value={money(totals.delivery_total ?? totals.site_work_subtotal ?? 0)} />
                    <Row label="Site Work" value={money(totals.site_work_total ?? totals.mandatory_services_subtotal ?? 0)} />
                    <Row label="Add-ons" value={money(totals.addons_total ?? totals.addons_subtotal ?? 0)} />
                    {(totals.discounts_total || 0) > 0 && (
                        <Row label="Discounts" value={`- ${money(totals.discounts_total)}`} />
                    )}
                    <div className="my-2 border-t border-slate-100" />
                    <Row label="Subtotal" value={money(totals.customer_subtotal ?? totals.subtotal ?? 0)} />
                    <Row label="Financed subtotal" value={money(totals.financed_subtotal || 0)} />
                    <Row label="Non-financed subtotal" value={money(totals.non_financed_subtotal || 0)} />
                    <Row label="Tax basis" value={money(totals.tax_basis ?? totals.customer_subtotal ?? totals.subtotal ?? 0)} />
                    <Row label={`3% sales tax (${(((totals.sales_tax_rate ?? 0) * 100) || 0).toFixed(2)}%)`} value={money(totals.sales_tax_total ?? totals.sales_tax ?? 0)} />
                    <div className="flex items-center justify-between rounded-lg bg-[#0F2A47] text-white px-3 py-2 mt-2">
                        <span className="text-xs uppercase tracking-wider">Estimated total</span>
                        <span className="font-display text-xl tabular" data-testid="totals-panel-grand-total">
                            {money(totals.estimated_total ?? totals.grand_total ?? 0)}
                        </span>
                    </div>
                </div>
            </div>

            <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Internal only</div>
                <div className="mt-2 space-y-2">
                    <Row label="Factory cost" value={money(totals.factory_cost || 0)} />
                    <Row label="Calculated EHS price" value={money(totals.ehs_price_calculated || 0)} />
                    <Row label="House gross margin" value={money(totals.house_gross_margin ?? totals.gross_margin ?? 0)} />
                    <Row label="Commissionable house margin" value={money(totals.commissionable_house_margin || 0)} />
                    <Row label="Service profit" value={money(totals.service_profit ?? totals.total_services_profit ?? 0)} />
                    <Row label="Admin fee (5%)" value={money(totals.admin_fee || 0)} />
                    <Row label="Loan fee" value={money(totals.loan_fee || 0)} />
                    <Row label="Salesperson commission (20%)" value={money(totals.salesperson_commission ?? totals.agent_commission ?? 0)} />
                    <div className={`flex items-center justify-between rounded-lg px-3 py-2 mt-2 border ${
                        totals.target_met
                            ? "bg-[rgba(22,122,74,0.10)] text-[#167A4A] border-[rgba(22,122,74,0.25)]"
                            : "bg-amber-50 text-amber-800 border-amber-200"
                    }`}>
                        <span className="text-xs uppercase tracking-wider flex items-center gap-1">
                            {totals.target_met ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                            Net take home
                        </span>
                        <span className="font-semibold tabular" data-testid="totals-panel-net-take-home">
                            {money(totals.net_take_home || 0)}
                        </span>
                    </div>
                    <div className="text-xs text-slate-500">
                        Floor: {money(totals.take_home_floor || 0)}
                    </div>
                </div>
            </div>
        </Card>
    );
}
